import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import type { FacetOption } from '@/types/FacetOption';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { useAppSelector } from '@/redux/hooks';
import { getMergedOptionFacet, getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { FilterAction } from '@/types/SearchFacet';
import { getBrowseDimensionKeyFromFacetType } from '@/utils/browsePath/browsePath';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';
import {
    filterFlatFacetOptionsOwnedByPrecedence,
    isFacetOptionCheckedForSemanticUrl,
    isFlatFacetOptionCheckedForCategoryIdUrl,
} from '@/utils/search/searchQuery';
import { urlSafeTitle } from '@/utils/urls';

type UseFlatFacetOptionsParams = {
    currentOpenMenu: CurrentOpenMenu;
    facetId: string;
    /**
     * Hardcoded flat facets (stylePeriod, creator, materialsAndTechniques) pass
     * their dedicated FilterAction values. Dynamic Attrs.* facets (subject)
     * omit these and fall back to the generic AttrFacet* actions -- same
     * defaults AttributeFacetFilter uses in the AllFilters drawer.
     */
    filterActionChanged?: FilterAction;
    filterActionRemoved?: FilterAction;
};

type UseFlatFacetOptionsResult = {
    filteredOptions: FacetOption[];
    isOptionChecked: (option: FacetOption) => boolean;
    onOptionClick: (option: FacetOption) => void;
};

/**
 * Shared multi-select-flat-facet wiring for the AllFilters drawer
 * (CreatorFilter / MaterialFilter / StyleAndPeriodFilter) and the
 * SearchFilters pill row (FacetOptionsPill).
 *
 * Returns sorted, count-pruned, precedence-filtered options plus
 * the checked-predicate and option-click navigation. Consumers own
 * the rendering shell -- the drawer wraps these in FacetCard +
 * useServerSideFacetExpansion; the pill wraps them in Chip +
 * ConditionalMenuContainer and applies a MAX_FILTER_OPTIONS_COUNT
 * client cap on top of `filteredOptions`.
 */
export const useFlatFacetOptions = ({
    currentOpenMenu,
    facetId,
    filterActionChanged = FilterAction.AttrFacetChanged,
    filterActionRemoved = FilterAction.AttrFacetRemoved,
}: UseFlatFacetOptionsParams): UseFlatFacetOptionsResult => {
    const { search } = useLocation();
    // Parameterize the generic so `queryParams` is already shaped as the
    // `Record<string, unknown>` the `is*Checked` helpers expect; avoids a
    // local cast at every call site.
    const queryParams = parseQueryParams<Record<string, unknown>>(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const facet = useAppSelector((state) => getMergedOptionFacet(state, facetId));
    const rawFacets = useAppSelector(getSearchFacets);
    const { options: mergedOptions = [] } = facet;

    const options = useMemo(
        () => filterFlatFacetOptionsOwnedByPrecedence<FacetOption>(mergedOptions, rawFacets, facetId),
        [mergedOptions, rawFacets, facetId]
    );

    const isOptionChecked = useCallback(
        (option: FacetOption) => {
            const checkedFromLegacyCategoryId = isFlatFacetOptionCheckedForCategoryIdUrl(
                queryParams,
                option.id,
                facetId,
                rawFacets,
                option.selected
            );
            return isFacetOptionCheckedForSemanticUrl(queryParams, facetId, option, checkedFromLegacyCategoryId);
        },
        [queryParams, rawFacets, facetId]
    );

    const onOptionClick = useCallback(
        (option: FacetOption) => {
            const newCheckStatus = !isOptionChecked(option);
            // Semantic browse facets (creator, style, material, origin) want a
            // SEO-friendly slug in the URL; dynamic Attrs.* facets fall through
            // to the raw option id since they have no semantic-URL mapping yet.
            const isSemanticBrowseFacet = Boolean(getBrowseDimensionKeyFromFacetType(facetId));
            const valueForUrl = isSemanticBrowseFacet ? urlSafeTitle(option.name ?? String(option.id)) : option.id;

            let newParams = modifyParamsWhenChecked(queryParams, facetId, valueForUrl, newCheckStatus, {
                useSemanticAttributeParams: true,
            });
            newParams = modifyParamsRemovePage(newParams);

            navigateWithParams({
                browseFacetOptionRemovedId: newCheckStatus ? undefined : option.id,
                currentOpenMenu,
                filterAction: newCheckStatus ? filterActionChanged : filterActionRemoved,
                params: newParams,
                preventScrollReset: true,
            });
        },
        [
            currentOpenMenu,
            facetId,
            filterActionChanged,
            filterActionRemoved,
            isOptionChecked,
            navigateWithParams,
            queryParams,
        ]
    );

    const filteredOptions = useMemo(
        () =>
            options
                .filter((option) => option.count !== 0 || isOptionChecked(option))
                .sort((firstOption, secondOption) => secondOption.count - firstOption.count),
        [options, isOptionChecked]
    );

    return { filteredOptions, isOptionChecked, onOptionClick };
};
